import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useChatAi } from "@/context/ChatAiContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, Plus, MessageSquare, Trash2, PanelLeft, Check, X, Clock, Paperclip, FileText, FolderPlus, RotateCcw, Cpu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import PageTour from "@/components/PageTour";

const CHAT_TOUR_STEPS = [
  { title: "Benvenuto nella Chat AI 🤖", body: "Ti mostro in pochi passi come farti aiutare dal tuo assistente. Puoi uscire con la X e rivedere la guida dal pulsante \"Guida\"." },
  { selector: "new-conversation-button", title: "Nuova conversazione", body: "Avvia una nuova chat quando vuoi cambiare argomento. Le conversazioni precedenti restano salvate nella cronologia qui a lato." },
  { selector: "chat-suggestion-0", title: "Suggerimenti pronti", body: "Non sai da dove iniziare? Tocca un suggerimento: l'AI può preparare bozze di risposta, riepilogare fatture o sollecitare preventivi." },
  { selector: "chat-input", title: "Scrivi la tua richiesta", body: "Chiedi in linguaggio naturale. L'AI lavora solo sulle comunicazioni di questo account e può creare bozze, inviare o programmare email per te." },
  { selector: "chat-send-button", title: "Invia", body: "Premi invio (o questo pulsante) per inviare. Per le azioni sensibili come l'invio di un'email, l'AI ti chiede sempre conferma prima di procedere." },
  { selector: "chatai-ai-disclosure", title: "Contenuti AI", body: "I testi sono generati con intelligenza artificiale: verifica sempre prima di inviare. Fatto! Riavvia la guida quando vuoi dal pulsante \"Guida\"." },
];

const openAttachment = async (fileId) => {
  if (!fileId) { toast.info("Allegato non disponibile per l'apertura."); return; }
  try {
    const r = await api.get(`/attachments/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error("Impossibile aprire l'allegato"); }
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// ---- Formattazione temporale (stile ChatGPT) ----
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    const t = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    if (sameDay(d, today)) return `Oggi, ${t}`;
    if (sameDay(d, yest)) return `Ieri, ${t}`;
    return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}, ${t}`;
  } catch { return ""; }
};
const fmtRelative = (iso) => {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "adesso";
    if (min < 60) return `${min} min fa`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} ${h === 1 ? "ora" : "ore"} fa`;
    const g = Math.floor(h / 24);
    if (g < 7) return `${g} ${g === 1 ? "giorno" : "giorni"} fa`;
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
};
const dayLabel = (iso) => {
  if (!iso) return "Precedenti";
  try {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    const weekAgo = new Date(); weekAgo.setDate(today.getDate() - 7);
    if (d.toDateString() === today.toDateString()) return "Oggi";
    if (d.toDateString() === yest.toDateString()) return "Ieri";
    if (d >= weekAgo) return "Ultimi 7 giorni";
    return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  } catch { return "Precedenti"; }
};

// Rende cliccabili, nel testo della risposta, i nomi file degli allegati citati.
function renderContent(content, attachments) {
  if (!attachments?.length) return content;
  const byName = {};
  for (const a of attachments) { if (a.filename && a.file_id) byName[a.filename] = a.file_id; }
  const names = Object.keys(byName).sort((x, y) => y.length - x.length);
  if (!names.length) return content;
  const rx = new RegExp(`(${names.map(escapeRegExp).join("|")})`, "gi");
  const parts = content.split(rx);
  return parts.map((part, i) => {
    const fid = byName[part] || byName[Object.keys(byName).find((n) => n.toLowerCase() === part.toLowerCase()) || ""];
    if (fid) {
      return (
        <button key={i} type="button" data-testid="chat-attachment-link" onClick={() => openAttachment(fid)}
          className="inline items-baseline font-medium text-[#007BFF] underline underline-offset-2 hover:text-[#0056B3] break-all">
          {part}
        </button>
      );
    }
    return part;
  });
}


const SUGGESTIONS = [
  "Prepara una bozza di risposta all'ultima email di reclamo, con tono cortese e disponibile",
  "Riepiloga le fatture in scadenza e gli importi",
  "Prepara una bozza per sollecitare il preventivo richiesto di recente",
];

export default function ChatAI() {
  const {
    messages, input, setInput, loading, sessionId, ask,
    confirmAction, cancelAction, startNew, openConversation, markLeft, enter,
  } = useChatAi();
  const { user } = useAuth();
  const firstName = (user?.name || user?.email || "").trim().split(/[\s@]+/)[0] || "";
  const greetName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : "";
  const [conversations, setConversations] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const endRef = useRef(null);
  const taRef = useRef(null);

  const loadConversations = useCallback(() => api.get("/chat/conversations").then((r) => setConversations(r.data)).catch((e) => console.debug("conv load", e)), []);

  // Ingresso/uscita dalla sezione: gestisce il reset dopo 120s di inattività fuori dalla chat.
  useEffect(() => {
    enter();
    return () => markLeft();
  }, [enter, markLeft]);

  // Ricarica la lista conversazioni al mount e ogni volta che una richiesta finisce (loading→false).
  useEffect(() => { if (!loading) loadConversations(); }, [loading, loadConversations]);

  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 220) + "px";
  }, [input]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const startNewLocal = () => { startNew(); setDrawerOpen(false); };
  const openConversationLocal = (conv) => { openConversation(conv); setDrawerOpen(false); };

  const deleteConversation = async (e, conv) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/conversations/${conv.id}`);
      if (conv.id === sessionId) startNewLocal();
      loadConversations();
    } catch (err) { console.debug("delete conv", err); }
  };

  const convList = (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <Button data-testid="new-conversation-button" onClick={startNewLocal} className="w-full rounded-md bg-[#007BFF] hover:bg-[#0056B3] h-10">
          <Plus className="h-4 w-4 mr-2" />Nuova conversazione
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto scroll-thin p-2 space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 py-1.5">Cronologia</div>
        {conversations.length === 0 && <div className="text-xs text-muted-foreground px-2 py-3">Nessuna conversazione salvata.</div>}
        {(() => {
          let lastGroup = null;
          return conversations.map((c) => {
            const g = dayLabel(c.updated_at || c.created_at);
            const showHeader = g !== lastGroup;
            lastGroup = g;
            return (
              <div key={c.id}>
                {showHeader && (
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold px-2 pt-3 pb-1 first:pt-1" data-testid={`conv-group-${g}`}>{g}</div>
                )}
                <button data-testid={`conversation-${c.id}`} onClick={() => openConversationLocal(c)}
                  className={`group w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-left transition-colors duration-150 ${c.id === sessionId ? "bg-[#EBF3FF] text-[#0056B3]" : "text-gray-700 hover:bg-secondary/70"}`}>
                  <MessageSquare className="h-4 w-4 shrink-0 opacity-60 mt-0.5" />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{c.title || "Conversazione"}</span>
                    <span className="block text-[10px] text-muted-foreground/80 truncate" data-testid={`conversation-time-${c.id}`} title={fmtDateTime(c.updated_at || c.created_at)}>{fmtRelative(c.updated_at || c.created_at)}</span>
                  </span>
                  <span onClick={(e) => deleteConversation(e, c)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600 transition-opacity duration-150 shrink-0" data-testid={`delete-conversation-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                </button>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] md:h-[100dvh]">
      {/* Conversations sidebar (desktop) */}
      <div className="w-[260px] min-w-[260px] hidden md:flex flex-col border-r border-border bg-gray-50/70">
        {convList}
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="px-4 sm:px-10 pt-3 sm:pt-4 pb-3 border-b border-border bg-card/50 flex items-start gap-3">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button data-testid="chat-conversations-drawer" className="md:hidden mt-1 p-2 -ml-2 text-gray-600"><PanelLeft className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[280px] bg-gray-50 flex flex-col">{convList}</SheetContent>
          </Sheet>
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-1.5">Assistente agente</div>
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tighter">Chat AI</h1>
            <p className="text-muted-foreground text-sm mt-1">Chiedi informazioni oppure fai agire l'assistente: può creare bozze, inviare o programmare email per te.</p>
            <PageTour storageKey="tour_chat_ai" className="mt-2" steps={CHAT_TOUR_STEPS} />
            <p className="text-[11px] text-muted-foreground/80 mt-2 flex items-center gap-1.5" data-testid="chatai-ai-disclosure">
              <Cpu className="h-3.5 w-3.5 text-[#007BFF] shrink-0" />
              Contenuti generati con intelligenza artificiale: verifica sempre prima di inviare.{" "}
              <a href="/legale?doc=ai" target="_blank" rel="noopener noreferrer" className="text-[#007BFF] hover:underline">Informativa AI</a>
            </p>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto scroll-thin px-4 sm:px-10 py-6 sm:py-8">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.length === 0 && !loading && (
              <div className="text-center py-10">
                <div className="h-14 w-14 rounded-md bg-[#007BFF] flex items-center justify-center mx-auto mb-5">
                  <Sparkles className="h-7 w-7 text-white" />
                </div>
                <h3 data-testid="chat-welcome" className="font-head text-xl font-semibold mb-2">
                  {greetName ? `Ciao ${greetName}, come posso aiutarti?` : "Chiedi qualsiasi cosa"}
                </h3>
                <p className="text-muted-foreground text-sm mb-6">Il tuo agente AI risponde e agisce solo sulle comunicazioni di questo account{user?.email ? ` (${user.email})` : ""}.</p>
                <div className="flex flex-col gap-2 max-w-md mx-auto">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => ask(s)}
                      className="text-sm text-left px-4 py-2.5 border border-border rounded-md hover:border-[#007BFF] hover:bg-[#007BFF]/5 transition-colors duration-200">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={m._k || i} data-testid={`chat-message-${i}`} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[80%] rounded-md px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-card border border-border"}`}>
                  {m.role === "assistant"
                    ? (m.content
                        ? <>{renderContent(m.content, m.attachments)}{m.streaming && <span className="inline-block w-1.5 h-3.5 ml-0.5 align-middle bg-[#007BFF] animate-pulse" />}</>
                        : <span className="inline-flex gap-1 items-center text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-[#007BFF] animate-bounce" style={{ animationDelay: "0ms" }} /><span className="h-1.5 w-1.5 rounded-full bg-[#007BFF] animate-bounce" style={{ animationDelay: "150ms" }} /><span className="h-1.5 w-1.5 rounded-full bg-[#007BFF] animate-bounce" style={{ animationDelay: "300ms" }} /></span>)
                    : m.content}
                </div>

                {(m.content || (m.role === "user")) && !m.streaming && (
                  <div className={`text-[10px] text-muted-foreground/70 mt-1 px-1 ${m.role === "user" ? "text-right" : "text-left"}`}
                    data-testid={`chat-message-time-${i}`} title={fmtDateTime(m.created_at || m.ts)}>
                    {fmtTime(m.created_at || m.ts)}
                  </div>
                )}
                {m.role === "assistant" && m.attachments?.length > 0 && (
                  <div data-testid={`chat-attachments-${i}`} className="mt-2 max-w-[80%] flex flex-wrap gap-2">
                    {m.attachments.map((a) => (
                      <button key={a.file_id} type="button" data-testid="chat-attachment-chip" onClick={() => openAttachment(a.file_id)}
                        title={`Apri ${a.filename}`}
                        className="inline-flex items-center gap-1.5 text-xs border border-border rounded-full px-3 py-1.5 bg-secondary/50 text-gray-700 hover:border-[#007BFF] hover:text-[#0056B3] transition-colors duration-150 max-w-full">
                        <Paperclip className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{a.filename}</span>
                      </button>
                    ))}
                  </div>
                )}

                {m.role === "assistant" && m.action_result && (
                  <div data-testid={`action-result-${i}`} className="mt-2 max-w-[80%] flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                    <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                    <span className="flex-1">{m.action_result.summary}</span>
                    {m.action_result.type === "create_draft" && (
                      <a href="/inbox" data-testid={`open-drafts-${i}`} className="font-semibold text-emerald-700 hover:underline shrink-0">Apri Bozze →</a>
                    )}
                  </div>
                )}

                {m.role === "assistant" && m.pending_action && (
                  <div data-testid={`pending-action-${i}`} className="mt-2 max-w-[80%] w-full border border-[#007BFF]/30 bg-[#007BFF]/5 rounded-md p-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#0056B3] mb-2">
                      {m.pending_action.type === "send_email" ? <Send className="h-3.5 w-3.5 shrink-0" />
                        : m.pending_action.type === "schedule_email" ? <Clock className="h-3.5 w-3.5 shrink-0" />
                        : m.pending_action.type === "create_pratica" ? <FolderPlus className="h-3.5 w-3.5 shrink-0" />
                        : m.pending_action.type === "close_pratica" ? <Check className="h-3.5 w-3.5 shrink-0" />
                        : m.pending_action.type === "reopen_pratica" ? <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                        : m.pending_action.type === "send_document" ? <FileText className="h-3.5 w-3.5 shrink-0" />
                        : m.pending_action.type === "send_to_supplier" ? <Send className="h-3.5 w-3.5 shrink-0" />
                        : <Trash2 className="h-3.5 w-3.5 shrink-0" />}
                      <span>{m.pending_action.summary}</span>
                    </div>
                    {m.pending_action.body && (
                      <p className="text-xs text-gray-600 whitespace-pre-wrap bg-white border border-border rounded p-2 mb-2 max-h-40 overflow-y-auto scroll-thin">{m.pending_action.body}</p>
                    )}
                    {m.pending_action.status === "pending" && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button data-testid={`confirm-action-${i}`} onClick={() => confirmAction(m)} className="h-8 bg-[#007BFF] hover:bg-[#0056B3] rounded-md text-xs px-3">
                          <Check className="h-3.5 w-3.5 mr-1" />{
                            m.pending_action.type === "schedule_email" ? "Conferma programmazione"
                            : m.pending_action.type === "move_trash" ? "Conferma"
                            : m.pending_action.type === "create_pratica" ? "Apri pratica"
                            : m.pending_action.type === "close_pratica" ? "Chiudi pratica"
                            : m.pending_action.type === "reopen_pratica" ? "Riapri pratica"
                            : m.pending_action.type === "send_document" ? "Invia documento"
                            : m.pending_action.type === "send_to_supplier" ? "Invia al fornitore"
                            : "Conferma invio"}
                        </Button>
                        <Button data-testid={`cancel-action-${i}`} onClick={() => cancelAction(m)} variant="outline" className="h-8 rounded-md text-xs px-3">
                          <X className="h-3.5 w-3.5 mr-1" />Annulla
                        </Button>
                        {m.pending_action.error && <span className="text-xs text-red-600 w-full">{m.pending_action.error}</span>}
                      </div>
                    )}
                    {m.pending_action.status === "working" && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Eseguo l'azione…</div>
                    )}
                    {m.pending_action.status === "done" && (
                      <div className="flex items-center gap-2 text-xs text-emerald-700"><Check className="h-3.5 w-3.5" />{m.pending_action.result_summary || "Fatto."}</div>
                    )}
                    {m.pending_action.status === "cancelled" && (
                      <div className="text-xs text-muted-foreground">Azione annullata.</div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading && !messages.some((m) => m.role === "assistant" && m.streaming) && (
              <div className="flex justify-start">
                <div className="bg-card border border-border rounded-md px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground ai-glow">
                  <Loader2 className="h-4 w-4 animate-spin text-[#007BFF]" />L'AI sta analizzando la cronologia…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        </div>

        <div className="border-t border-border p-5 bg-card">
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="max-w-3xl mx-auto flex items-end gap-3">
            <textarea
              ref={taRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              rows={1}
              placeholder="Scrivi la tua domanda…  (Invio per inviare, Maiusc+Invio per andare a capo)"
              className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-3 text-sm leading-relaxed min-h-[48px] max-h-[220px] overflow-y-auto scroll-thin focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007BFF]/50 placeholder:text-muted-foreground"
            />
            <Button data-testid="chat-send-button" type="submit" disabled={loading} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-12 px-5 shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
